<template>
  <el-dialog
    title="直营团长佣金设置"
    :visible.sync="dialogVisible"
    width="700px"
    append-to-body
    :close-on-click-modal="false"
    custom-class="external-fee-config-form-dialog"
    @close="handleClose"
  >
    <!-- 结算公式与生效规则说明，仅作业务提示，不参与表单提交。 -->
    <div class="fee-notice">
      <div>1、团长佣金=实际收入(已核销代金券金额+尾款) - 票商实际供应成本 - 平台扣点(实际收入*手填%) + 平台返点(实际收入*手填%) - 守兔扣点(实际收入*手填%) - 达人佣金(与cps佣金一致)</div>
      <div>2、订单结算时间与券订单结算佣金方式一致（每天凌晨4点结算当前 券状态为 已核销 未结佣的订单佣金）</div>
    </div>
    <el-form
      ref="formRef"
      :model="form"
      :rules="rules"
      hide-required-asterisk
      v-loading="detailLoading"
    >
      <!-- 团长支持多选；编辑时需要保留当前规则中已经配置的团长。 -->
      <el-form-item
        prop="userUUIDs"
        label="选择团长"
        label-width="75px"
        class="leader-form-item"
      >
        <el-select
          v-model="form.userUUIDs"
          multiple
          filterable
          collapse-tags
          clearable
          :loading="agentLoading"
          loading-text="正在加载中,请稍后"
          placeholder="请选择"
          style="width: 400px"
        >
          <el-option
            v-for="agent in agentOptions"
            :key="agent.userUUID"
            :label="agent.label"
            :value="agent.userUUID"
            :disabled="agent.disabled"
          />
        </el-select>
      </el-form-item>
      <!-- 三项费率共用相同布局与校验规则。 -->
      <div v-for="item in rateItems" :key="item.field" class="rate-section">
        <div class="rate-title">{{ item.label }}</div>
        <div class="rate-input-row">
          <span>按实际收入金额 ×</span>
          <el-form-item :prop="item.field" class="rate-validation">
            <el-input
              v-model="form[item.field]"
              maxlength="5"
              placeholder="0-50"
              @input="value => handleRateInput(item.field, value)"
            >
              <template slot="append">%</template>
            </el-input>
          </el-form-item>
        </div>
      </div>
    </el-form>
    <template slot="footer">
      <!-- 保存后立即生效的提示与提交操作区。 -->
      <div class="dialog-footer">
        <span class="effective-tip">设置成功后，立刻生效</span>
        <div>
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" :loading="saveLoading" :disabled="detailLoading" @click="handleSave">确认</el-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import poi from '@/api/poi'

const PoiModel = new poi()
// 三项费率的字段名，用于详情回显并统一维护字段映射。
const RATE_FIELDS = ['platformDeductRate', 'platformReturnRate', 'shoutuDeductRate']

// 从网络异常中提取可读文案；没有明确错误时使用业务兜底提示。
function getErrorMessage(error, fallback) {
  return error && error.response && error.response.data && error.response.data.message
    ? error.response.data.message
    : error && error.message
      ? error.message
      : fallback
}

// 兼容项目接口同时使用 code=200 和 success=true 的成功语义。
function isSuccess(response) {
  const body = response && response.data
  return !!body && (body.code === 200 || body.success === true)
}

// Http 请求返回完整 Axios response，此处统一取业务 data。
function getData(response) {
  return response && response.data ? response.data.data : null
}

export default {
  name: 'ExternalFeeConfigForm',
  data() {
    // 费率必填，范围为 0-50，最多保留两位小数。
    const validateRate = (rule, value, callback) => {
      if (value === undefined || value === null || String(value).trim() === '') {
        callback(new Error('请输入费率'))
        return
      }
      const text = String(value).trim()
      if (!/^\d+(?:\.\d{1,2})?$/.test(text)) {
        callback(new Error('请输入最多两位小数的数字'))
        return
      }
      const number = Number(text)
      if (!Number.isFinite(number) || number < 0 || number > 50) {
        callback(new Error('费率范围为0-50'))
        return
      }
      callback()
    }
    return {
      dialogVisible: false,
      isEdit: false,
      detailLoading: false,
      agentLoading: false,
      saveLoading: false,
      agentOptions: [],
      form: this.createEmptyForm(),
      rules: {
        userUUIDs: [{ required: true, type: 'array', min: 1, message: '请选择团长', trigger: 'change' }],
        platformDeductRate: [{ validator: validateRate, trigger: 'blur' }],
        platformReturnRate: [{ validator: validateRate, trigger: 'blur' }],
        shoutuDeductRate: [{ validator: validateRate, trigger: 'blur' }]
      },
      rateItems: [
        { field: 'platformDeductRate', label: '平台扣点计算规则' },
        { field: 'platformReturnRate', label: '平台返点计算规则' },
        { field: 'shoutuDeductRate', label: '守兔扣点计算规则' }
      ]
    }
  },
  methods: {
    // 新增和编辑共用同一表单，每次打开前创建干净数据，防止状态串用。
    createEmptyForm() {
      return {
        id: null,
        userUUIDs: [],
        platformDeductRate: '',
        platformReturnRate: '',
        shoutuDeductRate: ''
      }
    },
    // row 为空表示新增；row 存在表示编辑并需要查询配置详情。
    open(row) {
      this.isEdit = !!row
      this.form = this.createEmptyForm()
      this.agentOptions = []
      this.dialogVisible = true
      this.saveLoading = false
      this.detailLoading = !!row
      this.loadAgentOptions(row)
      if (row) this.loadDetail(row)
      else this.detailLoading = false
      this.$nextTick(() => {
        if (this.$refs.formRef) this.$refs.formRef.clearValidate()
      })
    },
    // 候选接口会过滤已配置团长，编辑时先合并当前团长以保证正常回显。
    async loadAgentOptions(row) {
      this.agentLoading = true
      try {
        const res = await PoiModel.getExternalFeeAgentList()
        if (!isSuccess(res)) {
          this.$message.error((res && res.data && (res.data.message || res.data.info)) || '获取可选团长失败')
          return
        }
        const list = this.toAgentArray(getData(res))
        const currentUuids = this.toStringArray(row && row.userUUIDs)
        const currentNames = this.toStringArray(row && row.agentNames)
        const currentUuidSet = new Set(currentUuids)
        this.mergeAgentOptions(currentUuids, currentNames)
        // 按 userUUID 合并候选项：不存在则追加，已存在则用接口最新数据覆盖。
        list.forEach(item => {
          const index = this.agentOptions.findIndex(option => option.userUUID === item.userUUID)
          // 编辑已有配置时，当前规则中的团长即使接口标记 disabled，也必须允许继续编辑。
          const option = currentUuidSet.has(item.userUUID)
            ? { ...item, disabled: false }
            : item
          if (index === -1) this.agentOptions.push(option)
          else this.$set(this.agentOptions, index, option)
        })
        if (row && currentUuids.length) this.form.userUUIDs = currentUuids
      } catch (error) {
        this.$message.error(getErrorMessage(error, '获取可选团长失败'))
      } finally {
        this.agentLoading = false
      }
    },
    // 编辑时按配置 id 查询完整数据，并回显团长与三项费率。
    async loadDetail(row) {
      try {
        const res = await PoiModel.getExternalFeeConfigDetail({ id: row.id })
        if (!isSuccess(res)) {
          this.$message.error((res && res.data && (res.data.message || res.data.info)) || '获取配置详情失败')
          return
        }
        const data = getData(res) || {}
        this.form.userUUIDs = this.toStringArray(data.userUUIDs || row.userUUIDs)
        this.mergeAgentOptions(this.form.userUUIDs, this.toStringArray(data.agentNames || row.agentNames))
        RATE_FIELDS.forEach(field => {
          this.form[field] = data[field] === undefined || data[field] === null ? '' : String(data[field])
        })
        this.form.id = data.id === undefined || data.id === null ? row.id : data.id
      } catch (error) {
        this.$message.error(getErrorMessage(error, '获取配置详情失败'))
      } finally {
        this.detailLoading = false
      }
    },
    // 输入阶段只保留数字和一个小数点，并限制最多两位小数。
    handleRateInput(field, value) {
      let text = String(value === undefined || value === null ? '' : value).replace(/[^0-9.]/g, '')
      const dotIndex = text.indexOf('.')
      if (dotIndex !== -1) {
        text = text.slice(0, dotIndex + 1) + text.slice(dotIndex + 1).replace(/\./g, '').slice(0, 2)
      }
      this.form[field] = text
    },
    // 新增保存时省略 id；编辑保存时必须携带详情返回的配置 id。
    async handleSave() {
      if (this.saveLoading || this.detailLoading) return
      let valid = false
      try {
        valid = await new Promise(resolve => {
          this.$refs.formRef.validate(result => resolve(result))
        })
      } catch (error) {
        valid = false
      }
      if (!valid) return
      const userUUIDs = Array.from(new Set(this.toStringArray(this.form.userUUIDs)))
      if (!userUUIDs.length) {
        this.$message.error('请选择团长')
        return
      }
      const payload = {
        userUUIDs,
        platformDeductRate: Number(this.form.platformDeductRate),
        platformReturnRate: Number(this.form.platformReturnRate),
        shoutuDeductRate: Number(this.form.shoutuDeductRate)
      }
      if (this.isEdit) {
        if (this.form.id === undefined || this.form.id === null || this.form.id === '') {
          this.$message.error('配置ID不能为空')
          return
        }
        payload.id = this.form.id
      }
      this.saveLoading = true
      try {
        const res = await PoiModel.saveExternalFeeConfig(payload)
        if (!isSuccess(res)) {
          this.$message.error((res && res.data && (res.data.message || res.data.info)) || '保存失败')
          return
        }
        this.$message.success('保存成功')
        this.dialogVisible = false
        this.$emit('success')
      } catch (error) {
        this.$message.error(getErrorMessage(error, '保存失败，请稍后重试'))
      } finally {
        this.saveLoading = false
      }
    },
    // 关闭后清理 loading 和校验状态，保证下次打开是干净表单。
    handleClose() {
      this.saveLoading = false
      this.detailLoading = false
      this.agentLoading = false
      this.$nextTick(() => {
        if (this.$refs.formRef) this.$refs.formRef.resetFields()
      })
    },
    // 将接口可能返回的数组或分隔字符串统一转换成非空字符串数组。
    toStringArray(value) {
      if (Array.isArray(value)) return value.filter(item => item !== undefined && item !== null && String(item).trim()).map(String)
      if (value === undefined || value === null || value === '') return []
      return String(value).split(/[,，、]/).map(item => item.trim()).filter(Boolean)
    },
    // 将候选团长转换为下拉选项，展示格式为“名称（手机号）”。
    toAgentArray(data) {
      const list = Array.isArray(data) ? data : (data && (data.list || data.records || data.data)) || []
      return list.map(item => {
        const userUUID = item.userUUID === undefined || item.userUUID === null ? '' : String(item.userUUID)
        const name = item.nickname || item.name || userUUID
        const label = item.mobile ? `${name}（${item.mobile}）` : name
        // 接口返回 disabled=true 的团长保留展示，但禁止在下拉框中选择。
        const disabled = item.disabled === true || item.disabled === 'true' || item.disabled === 1 || item.disabled === '1'
        return { userUUID, label, disabled }
      }).filter(item => item.userUUID)
    },
    // 按 userUUID 去重合并当前团长，避免编辑回显项被候选接口过滤掉。
    mergeAgentOptions(userUUIDs, names) {
      this.toStringArray(userUUIDs).forEach((userUUID, index) => {
        const option = this.agentOptions.find(item => item.userUUID === userUUID)
        if (option) {
          // 当前编辑配置中的团长始终允许调整，即使候选接口返回 disabled=true。
          this.$set(option, 'disabled', false)
          return
        }
        this.agentOptions.push({
          userUUID,
          label: names[index] || userUUID,
          disabled: false
        })
      })
    }
  }
}
</script>

<style scoped lang="less">
.fee-notice {
  margin: -4px 0 8px;
  padding: 8px 14px;
  color: #333;
  background: #f1f1f1;
  font-size: 12px;
  line-height: 20px;
}

.leader-form-item {
  margin-bottom: 20px;
}

.rate-section {
  margin: 20px 0 0 8px;
}

.rate-title {
  margin-bottom: 4px;
  color: #606266;
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
}

.rate-input-row {
  display: flex;
  align-items: flex-start;
  margin-left: 9px;
  color: #606266;
  font-size: 12px;
  line-height: 28px;
}

.rate-validation {
  width: 150px;
  margin: 0 8px;
}

.rate-validation .el-input {
  width: 150px;
}

.rate-validation .el-input__inner {
  height: 28px;
  padding: 0 8px;
  line-height: 28px;
}

.dialog-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 15px;
}

/deep/ .external-fee-config-form-dialog .el-dialog__body {
  padding-bottom: 0;
}

.effective-tip {
  margin-right: 14px;
  color: #a5a5a5;
  font-size: 12px;
}

.cancel-btn {
  color: #67c23a;
  border-color: #67c23a;
  background: #f0f9eb;
}
</style>



// WEBPACK FOOTER //
// src/views/pc/miniProgram/poiCps/components/externalFeeConfigForm.vue